/** O pop-up de cadastro e edição das abas de Ajustes (2026-10-20).
 *
 *  Antes o formulário ficava no TOPO de cada aba, em cima da lista: quem
 *  entrava via "Cadastrar porta-voz" antes de ver um único cadastro, e
 *  parecia que não havia nenhum; e editar um item do fim da lista jogava
 *  a pessoa de volta pro topo. Agora a lista vem primeiro, e criar ou
 *  editar abre aqui, sem tirar a pessoa do lugar.
 *
 *  **O formulário continua sendo da aba.** Os campos, o estado e o salvar
 *  são os mesmos de antes; este componente só dá a casca (título, corpo
 *  com rolagem, erro, rodapé com os obrigatórios e os botões). Assim cada
 *  aba mudou de LUGAR, não de lógica.
 *
 *  **Fechar não perde trabalho sem querer.** Clicar fora não fecha; o X,
 *  o Cancelar e o Esc fecham, mas pedem confirmação quando algo mudou
 *  desde a abertura. "Mudou" é comparar `values` com a foto tirada ao
 *  abrir — a aba diz o que é o formulário, e não um palpite por evento
 *  de digitação, que perderia chip removido e escolha feita numa lista.
 *
 *  **Renderiza num portal com a classe `ajustes-panel`**: os campos de
 *  Ajustes têm a aparência escopada por ela, e fora da árvore da página
 *  (o portal escapa da camada de vidro, que derruba `position: fixed`)
 *  eles perderiam o estilo. */

import { LoaderCircle, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import {
  RequiredFieldsHint,
  type RequiredField
} from "@/shared/components/RequiredFieldsHint";

type Props = {
  title: string;
  /** Overline acima do título ("PORTA-VOZES"). */
  eyebrow?: string;
  /** Texto curto sob o título. */
  description?: ReactNode;
  editing: boolean;
  /** O estado do formulário — a foto da abertura decide se fechar pede
   *  confirmação. Precisa ser serializável. */
  values: unknown;
  /** Salvando: trava fechar e salvar. */
  busy: boolean;
  required: readonly RequiredField[];
  saveLabel: string;
  error?: string | null;
  /** Formulário com muito campo (tema) pede mais largura. */
  wide?: boolean;
  onSave: () => void;
  onClose: () => void;
  children: ReactNode;
};

const snapshot = (values: unknown) => JSON.stringify(values);

export function SettingsFormDialog({
  title,
  eyebrow,
  description,
  editing,
  values,
  busy,
  required,
  saveLabel,
  error,
  wide = false,
  onSave,
  onClose,
  children
}: Props) {
  // A foto é tirada UMA vez, na montagem: a aba só monta o pop-up já com
  // os valores de abertura (vazios pra criar, os do item pra editar).
  const [initial] = useState(() => snapshot(values));
  const [confirming, setConfirming] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const dirty = snapshot(values) !== initial;

  const requestClose = () => {
    if (busy) return;
    if (dirty) setConfirming(true);
    else onClose();
  };

  // Lido por ref no listener, pra não rearmar a cada tecla digitada.
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (confirming) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // A lista aberta de um `Select` usa o Esc pra se fechar e marca o
      // evento; sem esta guarda o mesmo Esc fecharia o pop-up junto.
      if (event.key !== "Escape" || event.defaultPrevented) return;
      requestCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [confirming]);

  // Foco no primeiro campo, pra quem abriu já sair digitando.
  useEffect(() => {
    const first = bodyRef.current?.querySelector<HTMLElement>(
      "input:not([type=hidden]):not(:disabled), textarea:not(:disabled)"
    );
    first?.focus();
  }, []);

  const missing = required.some(field => !field.filled);

  return createPortal(
    <>
      <div className="modal-overlay settings-dialog-overlay">
        <div
          className={`modal-container ajustes-panel settings-dialog${wide ? " is-wide" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <header className="modal-header">
            <div>
              {eyebrow && <span className="eyebrow">{eyebrow}</span>}
              <h2>{title}</h2>
              {description && <p className="settings-dialog-description">{description}</p>}
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={requestClose}
              aria-label="Fechar"
              disabled={busy}
            >
              <X size={18} />
            </button>
          </header>
          <div className="modal-body settings-dialog-body" ref={bodyRef}>
            {error && <div className="login-error" role="alert">{error}</div>}
            {children}
          </div>
          <footer className="modal-footer settings-dialog-footer">
            <RequiredFieldsHint fields={required} editing={editing} />
            <button type="button" className="secondary-button" onClick={requestClose} disabled={busy}>
              Cancelar
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={onSave}
              disabled={busy || missing}
            >
              {busy && <LoaderCircle className="spin" size={16} />}
              {saveLabel}
            </button>
          </footer>
        </div>
      </div>
      {confirming && (
        <ConfirmDialog
          title="Descartar o que foi preenchido?"
          description={
            editing
              ? "As alterações deste formulário ainda não foram salvas."
              : "Este cadastro ainda não foi salvo."
          }
          confirmLabel="Descartar"
          danger
          onConfirm={() => {
            setConfirming(false);
            onClose();
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>,
    document.body
  );
}
